import {
	Button,
	Currency,
	Form,
	Header,
	Modal,
	Segment,
} from '@mairistem/composants';
import {
	Sens,
	type Tiers,
	TiersSearchInput,
	TYPE_TIERS_DEBITEUR_FOURNISSEUR,
	TYPE_TIERS_FOURNISSEUR,
} from '@mairistem/comptabilite';
import { getErrorMessage } from '@mairistem/finances-utils';
import React from 'react';
import { toast } from 'react-toastify';
import PROJET_ROW_TYPE from '../../../constants/common/projetRowType';
import type TiersCreditPaiement from '../../../interfaces/creditPaiement/tiersCreditPaiement';
import { getTiersCreditPaiement } from '../../../services/projet';
import RadioSegment from '../../common/RadioSegment';
import './CreditPaiementCreationEngagementsModal.css';
import { isNil } from 'lodash';

interface CreditPaiementCreationEngagementsModalProps {
	exercice: string;
	handleClose: () => void;
	identifiantProjet: number;
	montantRow: number;
	type: string;
}

const defaultTiers = { identifiant: 0 };

const CreditPaiementCreationEngagementsModal = ({
	// biome-ignore lint/correctness/noUnusedFunctionParameters: On va en avoire besoin par la suite
	exercice,
	handleClose,
	identifiantProjet,
	montantRow,
	type,
}: CreditPaiementCreationEngagementsModalProps) => {
	const [loadedPage, setLoadedPage] = React.useState(0);
	const [libelle, setLibelle] = React.useState('');
	const [loading, setLoading] = React.useState(false);
	const [loadingTiers, setLoadingTiers] = React.useState(false);
	const [montant, setMontant] = React.useState(0);
	const [tiers, setTiers] = React.useState<Tiers>(defaultTiers);
	const [tiersList, setTiersList] = React.useState<TiersCreditPaiement[]>([]);

	const fetchTiers = async (page: number) => {
		try {
			setLoadingTiers(true);
			const { data, meta } = await getTiersCreditPaiement(
				new URLSearchParams({
					identifiantProjet: String(identifiantProjet),
					page: String(page),
					sens: Sens.DEPENSE,
				}),
			);
			setLoadedPage(meta.lastPage === page ? 0 : page);
			setTiersList(() => [...data, ...tiersList]);
		} catch (e) {
			toast.error(
				getErrorMessage(e, 'Erreur lors de la récupération des tiers'),
			);
		} finally {
			setLoadingTiers(false);
		}
	};

	const handleBlurLibelle = ({
		target: { value },
	}: React.FocusEvent<HTMLInputElement>) => {
		setLibelle(value);
	};

	const handleBlurMontant = (
		_e: React.FocusEvent<HTMLInputElement>,
		value: number,
	) => {
		setMontant(value);
	};

	const handleChangeTiers = (t: Tiers) => {
		setTiers(t);
	};

	const handleConfirm = async () => {
		// Check
		if (montant === 0 || !libelle || tiers.identifiant === 0) {
			toast.error('Veuillez renseignez tout les champs');
			return;
		}

		if (montant > montantRow) {
			toast.error(
				`Le montant ne peut pas dépasser le montant de l'${type === PROJET_ROW_TYPE.PROJET ? 'autorisation' : 'évènement'}`,
			);
			return;
		}

		try {
			setLoading(true);
			toast.success("L'engagement a été crée avec succès");
			handleClose();
		} catch (e) {
			toast.error(
				getErrorMessage(e, "Erreur lors de la création de l'engagement"),
			);
		} finally {
			setLoading(false);
		}
	};

	const handleLoadTiers = () => {
		fetchTiers(loadedPage + 1);
	};

	const handleResetTiers = () => {
		setTiers(defaultTiers);
	};

	return (
		<Modal
			closeIcon
			closeOnDimmerClick={false}
			onClose={handleClose}
			onMount={() => fetchTiers(1)}
			open
			size="tiny"
		>
			<Modal.Header content="Créer un engagement comptable" />
			<Modal.Content>
				<Form>
					<Form.Group widths="equal">
						<Form.Field
							control={Currency}
							data-testid="creditPaiementCreationEngagementModalMontant"
							label="Montant"
							onBlur={handleBlurMontant}
							required
						/>
						<Form.Input
							data-testid="creditPaiementCreationEngagementModalLibelle"
							label="Libellé"
							placeholder="Description de l'engagement"
							onBlur={handleBlurLibelle}
							required
						/>
					</Form.Group>
				</Form>
				<Header
					content="Fournisseur"
					subheader="Fournisseurs déjà utilisés pour cette autorisation:"
					size="tiny"
				/>
				{loadingTiers ? (
					<Segment loading />
				) : (
					<>
						{tiersList.length === 0 && (
							<Segment>
								Aucun fournisseur trouvé pour cette autorisation
							</Segment>
						)}
						<Segment basic className="tiersList">
							{tiersList.map((t) => (
								<RadioSegment
									checked={tiers.identifiant === t.identifiant}
									content={t.nom}
									data-testid={`creditPaiementCreationEngagementModalTiers${t.identifiant}`}
									key={t.identifiant}
									onClick={() => setTiers(t)}
									subheader={isNil(t.siret) ? '' : `SIRET: ${t.siret}`}
								/>
							))}
						</Segment>
						{loadedPage > 0 && (
							<Button
								content="Afficher plus"
								fluid
								onClick={() => handleLoadTiers()}
							/>
						)}
						<Segment>
							<TiersSearchInput
								adresses={['F']}
								debiteurOuFournisseur={[
									TYPE_TIERS_FOURNISSEUR,
									TYPE_TIERS_DEBITEUR_FOURNISSEUR,
								]}
								input={{ fluid: true }}
								label="Rechercher un autre fournisseur"
								onChange={handleChangeTiers}
								onReset={handleResetTiers}
								tiers={tiers}
							/>
						</Segment>
					</>
				)}
			</Modal.Content>
			<Modal.Actions>
				<Button
					content="Annuler"
					data-testid="creditPaiementCreationEngagementModalCancelButton"
					onClick={() => handleClose()}
				/>
				<Button
					content="Créer l'engagement"
					data-testid="creditPaiementCreationEngagementModalCreateButton"
					disabled={loading}
					loading={loading}
					onClick={() => handleConfirm()}
					positive
				/>
			</Modal.Actions>
		</Modal>
	);
};

export default CreditPaiementCreationEngagementsModal;
